import { splitH3Entries } from "../parse-sections";
import type { Container, ContainerKind, DataFlow, FlowStep } from "../types";
import { parseAssumptions } from "./parse-assumptions-v3";
import { asString, asStringArray, parseBoldEntriesV3 } from "./parse-bullets-v3";
import { parseDataModel } from "./parse-data-model-v3";
import { parseRisks } from "./parse-risks-v3";
import { parseFlowSteps } from "./parse-steps-v3";

// ## L2 — Containers
// ### Containers    (one or more containers with kind, tech_stack, risks, assumptions)
// ### Data Flows    (one or more from→to edges with metadata + optional risks)

interface L2Result {
  containers: Container[];
  dataFlows: DataFlow[];
}

const CONTAINER_KIND_VALUES: ReadonlySet<ContainerKind> = new Set([
  "service",
  "api",
  "frontend",
  "database",
  "worker",
  "job",
  "queue",
  "cache",
  "storage",
  "ml-model",
  "gateway",
  "other",
]);

export function parseL2(body: string): L2Result {
  const result: L2Result = { containers: [], dataFlows: [] };
  for (const sub of splitH3Entries(body)) {
    const title = sub.title.toLowerCase().trim();
    if (title === "containers") {
      result.containers = parseContainers(sub.body);
    } else if (title === "data flows") {
      result.dataFlows = parseDataFlows(sub.body);
    }
  }
  return result;
}

function parseContainers(body: string): Container[] {
  const entries = parseBoldEntriesV3(body, "L2.Containers");
  return entries.map((entry) => {
    const p = entry.properties;
    const scope = `L2.Containers.${entry.id}`;
    const rawKind = (asString(p.kind) ?? "").toLowerCase();
    const kind: ContainerKind = CONTAINER_KIND_VALUES.has(rawKind as ContainerKind)
      ? (rawKind as ContainerKind)
      : "other";
    const tech_stack = asStringArray(p.tech_stack);
    const risks = parseRisks(p.risks, { scope, strict: true });
    const assumptions = parseAssumptions(p.assumptions, {
      scope,
      strict: true,
    });
    // Optional data model — only meaningful on data-storage kinds, but parsed
    // wherever present. `parseDataModel` returns undefined when the block is
    // absent or has no parseable entities.
    //
    // Salvage: a very common authoring mistake is under-indenting `entities:`
    // (and `relationships:`) so they land at the SAME column as `data_model:`
    // instead of deeper. YAML then parses `data_model` as null and lifts
    // `entities`/`relationships` to the container's properties. Reconstruct
    // the intended object when we see that exact shape — neither key is a
    // valid top-level container property, so this is unambiguous.
    let dataModelInput: unknown = p.data_model;
    if (dataModelInput === null && Array.isArray(p.entities)) {
      dataModelInput = { entities: p.entities, relationships: p.relationships };
      console.warn(
        `[arch-parser] container "${entry.id}": data_model child keys (entities/relationships) were under-indented to the same column as \`data_model:\`. Salvaged the block, but indent them two more spaces in the source markdown so YAML can read them as children.`,
      );
    }
    const dataModel = parseDataModel(dataModelInput);
    return {
      id: entry.id,
      label: asString(p.label) ?? entry.id,
      headline: asString(p.headline),
      description: asString(p.description),
      why: asString(p.why),
      kind,
      tech_stack,
      risks,
      assumptions,
      dataModel,
      // v0.3 doesn't ship these on containers but the interface keeps them
      // required for renderer compat — populate defaults that mirror the
      // canonical fields so the renderer can read either vocabulary.
      type: legacyTypeFor(kind),
      technology: tech_stack,
      risk: risks.map((r) => r.description),
      concerns: [],
      components: [],
    };
  });
}

// Map v0.3 ContainerKind → legacy ContainerType so the existing renderer
// (which reads `c.type` for icons / palette) continues to work. This
// mirrors the inverse of `CONTAINER_TYPE_TO_KIND` in legacy-mapper.ts.
function legacyTypeFor(
  kind: ContainerKind,
):
  | "frontend"
  | "backend"
  | "api"
  | "database"
  | "cache"
  | "queue"
  | "worker"
  | "gateway"
  | "storage"
  | "cdn"
  | "other" {
  switch (kind) {
    case "service":
      return "backend";
    case "ml-model":
    case "job":
      return "worker";
    case "frontend":
    case "api":
    case "database":
    case "cache":
    case "queue":
    case "worker":
    case "gateway":
    case "storage":
      return kind;
    default:
      return "other";
  }
}

function synthesizeStepsIfEmpty(
  parsed: FlowStep[],
  properties: Record<string, unknown>,
  fallbackId: string,
): FlowStep[] {
  if (parsed.length > 0) return parsed;
  const from = asString(properties.from);
  const to = asString(properties.to);
  if (!from || !to) return [];
  return [
    {
      number: 1,
      source: from,
      target: to,
      description: asString(properties.what_data) ?? asString(properties.label) ?? fallbackId,
    },
  ];
}

function parseDataFlows(body: string): DataFlow[] {
  const entries = parseBoldEntriesV3(body, "L2.DataFlows");
  return entries.map((entry) => {
    const p = entry.properties;
    const scope = `L2.DataFlows.${entry.id}`;
    const risks = parseRisks(p.risks, { scope, strict: true });
    return {
      id: entry.id,
      label: asString(p.label) ?? entry.id,
      from: asString(p.from),
      to: asString(p.to),
      what_data: asString(p.what_data),
      protocol: asString(p.protocol),
      frequency: asString(p.frequency),
      why_this_shape: asString(p.why_this_shape),
      risks: risks.length > 0 ? risks : undefined,
      // Choreography. v0.3 flows that have been upgraded from v0.2 (or
      // authored from scratch with the multi-hop pattern from the ND
      // prompt) carry a `steps:` block whose entries are real edges in
      // the diagram. Parse them.
      //
      // Fallback: when the markdown has no `steps:` block but provides
      // top-level from/to, synthesize a single step so downstream
      // consumers (FlowPlayer, DetailPanel, canonicalEdges) see at least
      // one edge. The synthesized step is indistinguishable from a
      // hand-authored single-step flow; canonicalEdges treats both as
      // "one edge per step" — same wire-format outcome.
      steps: synthesizeStepsIfEmpty(parseFlowSteps(p.steps, { scope, strict: true }), p, entry.id),
    };
  });
}
